import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation,
  faChevronDown,
  faChevronUp,
  faLock,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { useUpgradeModal } from '../UpgradeModal';

// Severity chip — colors follow GapWarningBadge (high red / medium amber /
// low blue).
const SEVERITY_CHIP = {
  high: 'text-red-700 bg-red-50 border-red-200',
  medium: 'text-amber-700 bg-amber-50 border-amber-200',
  low: 'text-blue-700 bg-blue-50 border-blue-200',
};

export function SeverityChip({ severity }) {
  return (
    <span
      className={`shrink-0 text-[9px] font-bold uppercase tracking-wider rounded-full border px-1.5 py-0.5 ${SEVERITY_CHIP[severity] || SEVERITY_CHIP.low}`}
    >
      {severity}
    </span>
  );
}

// SefaBanner-style amber panel listing regulatory data gaps, each with a
// click-to-fix action: free gaps link to the page that fixes them, premium
// gaps open the upgrade modal.
export default function GapList({
  gaps,
  title = 'Data gaps',
  collapsible = false,
  defaultCollapsed = false,
  maxItems,
}) {
  const { openUpgrade } = useUpgradeModal();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  if (!gaps || gaps.length === 0) return null;

  const shown = maxItems ? gaps.slice(0, maxItems) : gaps;
  const hiddenCount = gaps.length - shown.length;

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 overflow-hidden">
      <button
        type="button"
        onClick={() => collapsible && setCollapsed((v) => !v)}
        className={`w-full flex items-center gap-2 px-4 py-2.5 bg-transparent border-0 text-left ${
          collapsible ? 'cursor-pointer hover:bg-amber-100/50' : 'cursor-default'
        }`}
      >
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          className="text-amber-500"
        />
        <span className="text-xs font-semibold text-amber-900 flex-1">
          {title} ({gaps.length})
        </span>
        {collapsible && (
          <FontAwesomeIcon
            icon={collapsed ? faChevronDown : faChevronUp}
            className="text-amber-400 text-xs"
          />
        )}
      </button>

      {!(collapsible && collapsed) && (
        <ul className="list-none m-0 px-4 pb-3 pt-0 flex flex-col gap-1.5">
          {shown.map((gap) => (
            <li
              key={gap.id}
              className="flex items-center gap-2.5"
            >
              <SeverityChip severity={gap.severity} />
              <span className="flex-1 text-xs text-slate-700 min-w-0">{gap.message}</span>
              {gap.fix?.feature ? (
                <button
                  type="button"
                  onClick={() => openUpgrade(gap.fix.feature)}
                  className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-primaryColor bg-transparent border-0 hover:underline"
                >
                  <FontAwesomeIcon
                    icon={faLock}
                    className="text-[9px]"
                  />
                  Unlock
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="text-[9px]"
                  />
                </button>
              ) : (
                <Link
                  to={gap.fix.path}
                  className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-primaryColor no-underline hover:underline"
                >
                  Fix
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="text-[9px]"
                  />
                </Link>
              )}
            </li>
          ))}
          {hiddenCount > 0 && <li className="text-[11px] text-amber-700">+{hiddenCount} more</li>}
        </ul>
      )}
    </div>
  );
}
